import { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getAttribution, newSubmissionId } from "../lib/attribution";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Star, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { LOCATIONS, hgLink, trackOutbound, trackConversion, trackPhoneClick, HG_TRIAL_PATH, HG_MEMBERSHIP_PATH, HG_KIDS_PATH, GOOGLE_REVIEWS_LINK } from "../data/site";
import { PROGRAMS, programBySlug } from "../data/programs";
import { SCHEDULE, DAYS, COACHES, REVIEWS } from "../data/content";
import { TrialButton, Eyebrow, Reveal } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Program Finder Quiz ---------------- */
const QUIZ_GOALS = [
  { id: "shape", label: "Get in shape / a different kind of workout", rec: "muay-thai-kickboxing", note: "Muay Thai fundamentals is the highest-calorie-burn hour on our schedule — and you learn a skill while you sweat." },
  { id: "defense", label: "Learn to defend myself", rec: "brazilian-jiu-jitsu", note: "Start with BJJ fundamentals plus one striking class a week. That combination covers the standing fight and the ground." },
  { id: "compete", label: "Train to compete (amateur or pro)", rec: "mma", note: "Build your base discipline first, then Guy's MMA room ties it together with a real competition pathway." },
  { id: "kid", label: "My kid needs discipline and confidence", rec: "kids-martial-arts", note: "Kids Combat Sports (7+) or Lion Cubs (5–6). Structured, safe, and parents watch every class." },
  { id: "beginner", label: "I'm a total beginner", rec: "boxing", note: "Boxing fundamentals is the cleanest first step — stance, movement, and confidence with zero pressure." },
  { id: "private", label: "Private one-on-one coaching", rec: "private-lessons", note: "One-on-one with Guy or a senior coach — the fastest way to build technique on your schedule." },
];

export const ProgramFinderQuiz = () => {
  const [goal, setGoal] = useState(null);
  const [loc, setLoc] = useState("richardson");
  const rec = goal ? programBySlug(goal.rec) : null;
  return (
    <div className="border border-black/10 bg-white" data-testid="program-finder-quiz">
      <div className="p-6 md:p-10">
        <Eyebrow>Program Finder</Eyebrow>
        <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-2">What are you here for?</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          {QUIZ_GOALS.map((g) => (
            <button key={g.id} onClick={() => setGoal(g)} data-testid={`program-quiz-option-${g.id}`}
              className={`text-left p-4 border transition-all duration-200 font-medium ${goal?.id === g.id ? "border-white/40 bg-black/5 text-[#0A0B0D]" : "border-black/15 hover:border-black/40"}`}>
              {g.label}
            </button>
          ))}
        </div>
        <AnimatePresence>
          {rec && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-8 bg-[#0A0B0D] text-white p-6 md:p-8" data-testid="program-quiz-result">
              <p className="font-mono text-xs tracking-[0.22em] uppercase text-neutral-400">Our recommendation</p>
              <h3 className="font-display font-black uppercase text-2xl sm:text-3xl mt-2">{rec.name}</h3>
              <p className="mt-2 text-white/70">{goal.note}</p>
              <p className="mt-2 text-white/50 text-sm">{rec.level} · Ages {rec.ages}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="font-mono text-xs uppercase tracking-widest text-white/50">Location:</span>
                {LOCATIONS.map((l) => (
                  <button key={l.slug} onClick={() => setLoc(l.slug)} data-testid={`quiz-location-${l.slug}`}
                    className={`px-4 py-2 text-sm font-bold uppercase tracking-wide border transition-colors ${loc === l.slug ? "border-white bg-white text-[#0A0B0D]" : "border-white/30 text-white/70 hover:text-white"}`}>
                    {l.short}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <TrialButton campaign={`quiz-${rec.slug}`} page="quiz" testId="quiz-trial-cta-btn">Book Your Free Trial at Hidden Gym</TrialButton>
                <Link to={`/programs/${rec.slug}`} className="inline-flex items-center border border-white/30 text-white font-display font-bold uppercase tracking-wide px-6 py-4 hover:bg-white/10 transition-colors" data-testid="quiz-learn-more-link">
                  Learn About {rec.name}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* ---------------- Filterable Schedule ---------------- */
const FilterGroup = ({ label, options, value, onChange, testPrefix }) => (
  <div>
    <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#5A6270] mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      <button onClick={() => onChange("all")} data-testid={`${testPrefix}-all`}
        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${value === "all" ? "bg-[#0A0B0D] text-white border-[#0A0B0D]" : "border-black/20 text-[#5A6270] hover:border-black/50"}`}>All</button>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)} data-testid={`${testPrefix}-${o.value}`}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${value === o.value ? "bg-[#0A0B0D] text-white border-[#0A0B0D]" : "border-black/20 text-[#5A6270] hover:border-black/50"}`}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const SCHED_DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const ScheduleFilterable = ({ lockLocation, lockProgram, compact = false }) => {
  const [loc, setLoc] = useState(lockLocation || "all");
  const [prog, setProg] = useState(lockProgram || "all");
  const [day, setDay] = useState("all");
  const [level, setLevel] = useState("all");
  const [age, setAge] = useState("all");

  const DAY_ORDER = SCHED_DAY_ORDER;
  const toMins = (t) => {
    const [hm, ap] = t.split(" ");
    let [h, m] = hm.split(":").map(Number);
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return h * 60 + m;
  };
  const rows = useMemo(() => SCHEDULE.filter((s) =>
    (loc === "all" || s.loc === loc) &&
    (prog === "all" || s.slug === prog) &&
    (day === "all" || s.day === day) &&
    (level === "all" || s.level === level) &&
    (age === "all" || s.age === age)
  ).sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day) || toMins(a.time) - toMins(b.time)), [loc, prog, day, level, age]);

  return (
    <div data-testid="filterable-schedule">
      <p className="mb-4 inline-block bg-neutral-100 text-neutral-700 text-xs font-mono px-3 py-1.5 border border-neutral-300" data-testid="schedule-placeholder-flag">
        Official Hidden Gym schedule (v9.28.2026). Level 2 & 3 classes require coach approval — trial guests join Level 1 / fundamentals classes.
      </p>
      <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"} mb-6`}>
        {!lockLocation && <FilterGroup label="Location" testPrefix="schedule-location-filter" value={loc} onChange={setLoc}
          options={LOCATIONS.map((l) => ({ value: l.slug, label: l.short }))} />}
        {!lockProgram && <FilterGroup label="Discipline" testPrefix="schedule-program-filter" value={prog} onChange={setProg}
          options={PROGRAMS.filter((p) => p.slug !== "private-lessons").map((p) => ({ value: p.slug, label: p.name.split(" (")[0].split(" /")[0] }))} />}
        <FilterGroup label="Day" testPrefix="schedule-day-filter" value={day} onChange={setDay} options={DAYS.map((d) => ({ value: d, label: d }))} />
        <FilterGroup label="Level" testPrefix="schedule-level-filter" value={level} onChange={setLevel}
          options={["All", "Intermediate", "Advanced"].map((l) => ({ value: l, label: l }))} />
        <FilterGroup label="Age Group" testPrefix="schedule-age-filter" value={age} onChange={setAge}
          options={[{ value: "Adults", label: "Adults" }, { value: "Ages 7–14", label: "Kids 7–14" }, { value: "Ages 5–6", label: "Ages 5–6" }, { value: "Adults · Members only", label: "Members" }]} />
      </div>
      <div className="border border-black/10 divide-y divide-black/5 bg-white" role="table" aria-label="Class schedule">
        {rows.length === 0 && <p className="p-6 text-[#5A6270]" data-testid="schedule-empty-state">No classes match those filters — try widening your search.</p>}
        {rows.map((s, i) => (
          <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center p-4 hover:bg-[#F7F8F9] transition-colors" role="row" data-testid={`schedule-row-${i}`}>
            <div className="font-display font-black uppercase text-lg">{s.day} <span className="text-neutral-400">{s.time}</span></div>
            <div className="md:col-span-2">
              <Link to={`/programs/${s.slug}`} className="font-bold hover:text-neutral-500">{s.label}</Link>
              <p className="text-xs text-[#5A6270]">{s.age}</p>
            </div>
            <div className="text-sm text-[#5A6270]">{s.level}</div>
            <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-1 text-[#5A6270]"><MapPin size={12} /> {LOCATIONS.find((l) => l.slug === s.loc)?.short}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Rotating Review Quote Strip ---------------- */
export const ReviewQuoteStrip = ({ programSlug }) => {
  const quotes = useMemo(() => REVIEWS.filter((r) => r.tags?.includes(programSlug)), [programSlug]);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % quotes.length), 6000);
    return () => clearInterval(t);
  }, [quotes.length]);
  if (quotes.length === 0) return null;
  const q = quotes[i];
  return (
    <div className="bg-[#0A0B0D] text-white border-y border-white/10" data-testid="review-quote-strip" aria-live="polite">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex text-[#F59E0B] shrink-0" aria-label="5 stars">
          {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={16} fill="currentColor" />)}
        </div>
        <AnimatePresence mode="wait">
          <motion.blockquote key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.45 }}
            className="flex-1 text-sm sm:text-base text-white/85 leading-relaxed">
            "{q.text}" <span className="block sm:inline font-mono text-[11px] uppercase tracking-wider text-white/45 sm:ml-2">— {q.source}</span>
          </motion.blockquote>
        </AnimatePresence>
        {quotes.length > 1 && (
          <div className="flex gap-1.5 shrink-0" role="tablist" aria-label="Review quotes">
            {quotes.map((_, d) => (
              <button key={d} onClick={() => setI(d)} aria-label={`Show quote ${d + 1}`} data-testid={`quote-strip-dot-${d}`}
                className={`w-2 h-2 rounded-full transition-colors ${d === i ? "bg-white text-[#0A0B0D]" : "bg-white/25 hover:bg-white/50"}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Review Carousel ---------------- */
export const ReviewCarousel = () => {
  const [i, setI] = useState(0);
  const per = 3;
  const pages = Math.ceil(REVIEWS.length / per);
  const view = REVIEWS.slice(i * per, i * per + per);
  return (
    <div data-testid="review-carousel">
      <div className="grid md:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {view.map((r, idx) => (
            <motion.figure key={`${i}-${idx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
              className="bg-white border border-black/10 p-6 flex flex-col">
              <div className="flex text-[#F59E0B]" aria-label={`${r.stars} stars`}>
                {Array.from({ length: r.stars }).map((_, s) => <Star key={s} size={16} fill="currentColor" />)}
              </div>
              <blockquote className="mt-4 text-[#374151] leading-relaxed flex-1">"{r.text}"</blockquote>
              <figcaption className="mt-4 text-sm">
                <span className="font-bold">{r.program}</span>
                <p className="font-mono text-[11px] uppercase tracking-wider text-[#5A6270] mt-1">{r.source}</p>
              </figcaption>
            </motion.figure>
          ))}
        </AnimatePresence>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-[#5A6270]">
          Real excerpts from public Google reviews.{" "}
          <a href={GOOGLE_REVIEWS_LINK} target="_blank" rel="noopener noreferrer" className="underline hover:text-neutral-500" data-testid="read-all-reviews-link">
            Read them all on Google
          </a>
        </p>
        <div className="flex gap-2">
          <button onClick={() => setI((i - 1 + pages) % pages)} aria-label="Previous reviews" data-testid="reviews-prev-btn" className="p-2 border border-black/20 hover:bg-[#0A0B0D] hover:text-white transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={() => setI((i + 1) % pages)} aria-label="Next reviews" data-testid="reviews-next-btn" className="p-2 border border-black/20 hover:bg-[#0A0B0D] hover:text-white transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Coach Cards (link to spotlight pages) ---------------- */
const coachInitials = (name) => name.split(" ").map((n) => n[0]).join("");

export const CoachMonogram = ({ coach, className = "", testIdPrefix = "coach-monogram" }) => (
  <div className={`w-full h-full bg-[#0A0B0D] flex flex-col items-center justify-center ${className}`} data-testid={`${testIdPrefix}-${coach.slug}`}>
    <span className="font-display font-black text-5xl text-white/90">{coachInitials(coach.name)}</span>
    <span className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40">{coach.role}</span>
  </div>
);

export const CoachCards = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
    {COACHES.map((c, idx) => (
      <Reveal key={c.slug} delay={idx * 0.08}>
        <Link to={`/coaches/${c.slug}`} data-testid={`coach-card-${c.slug}`}
          className="group block w-full bg-white border border-black/10 hover:border-[#0A0B0D] transition-colors overflow-hidden">
          <div className="aspect-[4/3] overflow-hidden">
            {c.image ? (
              <img src={c.image} alt={`${c.name}, ${c.role} at Mezger Martial Arts`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <CoachMonogram coach={c} />
            )}
          </div>
          <div className="p-5">
            <h3 className="font-display font-black uppercase text-xl">{c.name}</h3>
            <p className="text-neutral-400 font-mono text-xs uppercase tracking-widest mt-1">{c.role}</p>
            <p className="text-sm text-[#374151] mt-2 leading-relaxed">{c.card}</p>
            <p className="mt-3 text-sm font-bold group-hover:text-neutral-500 transition-colors">Full story & lineage →</p>
          </div>
        </Link>
      </Reveal>
    ))}
  </div>
);

/* ---------------- Membership Handoff Cards ---------------- */
const OFFERS = [
  { id: "trial", name: "Free Trial Class", desc: "Your first class is free at either location. No card, no commitment — just show up ready to train.", detail: "Includes gloves & pads · Any fundamentals class", popular: false, path: HG_TRIAL_PATH, campaign: "membership-trial", cta: "Book Free Trial" },
  { id: "unlimited", name: "Monthly Unlimited", desc: "Unlimited martial arts classes plus full Hidden Gym access — gym floor, strength equipment, and open mat.", detail: "Current rates at Hidden Gym — inquire below", popular: true, path: HG_MEMBERSHIP_PATH, campaign: "membership-unlimited", cta: "Join at Hidden Gym" },
  { id: "kids", name: "Kids Membership", desc: "Kids Combat Sports (7+) or Lion Cubs (5–6), with coach-tracked progression and family-friendly scheduling.", detail: "Current rates at Hidden Gym — inquire below", popular: false, path: HG_KIDS_PATH, campaign: "membership-kids", cta: "Enroll Your Kid" },
  { id: "privates", name: "Private Lesson Packs", desc: "One-on-one sessions with Guy or senior coaches. Tell us your goal and we'll match you with the right coach.", detail: "Rates vary by coach — inquire", popular: false, path: "/programs/private-lessons", campaign: "membership-privates", cta: "Inquire", internal: true },
];

export const MembershipCards = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
    {OFFERS.map((o, idx) => (
      <Reveal key={o.id} delay={idx * 0.08} className="h-full">
        <div className={`h-full flex flex-col border p-6 relative ${o.popular ? "border-white/40 bg-[#0A0B0D] text-white" : "border-black/10 bg-white"}`} data-testid={`membership-card-${o.id}`}>
          {o.popular && <span className="absolute -top-3 left-6 bg-white text-[#0A0B0D] text-xs font-bold uppercase tracking-widest px-3 py-1">Most Popular</span>}
          <h3 className="font-display font-black uppercase text-xl">{o.name}</h3>
          <p className={`mt-2 text-sm leading-relaxed flex-1 ${o.popular ? "text-white/70" : "text-[#374151]"}`}>{o.desc}</p>
          <p className={`mt-3 font-mono text-xs uppercase tracking-wider ${o.popular ? "text-white/50" : "text-[#5A6270]"}`}>{o.detail}</p>
          <Link to={o.internal ? o.path : hgLink(o.path, o.campaign)} onClick={() => !o.internal && trackOutbound("membership", o.campaign, o.id === "trial")} data-testid={`membership-card-${o.id}-cta`}
            className={`mt-5 text-center font-display font-bold uppercase tracking-wide py-3 transition-colors ${o.popular ? "bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white" : "border border-[#0A0B0D] hover:bg-[#0A0B0D] hover:text-white"}`}>
            {o.cta}
          </Link>
        </div>
      </Reveal>
    ))}
  </div>
);

/* ---------------- Private Lesson Lead Form ---------------- */
export const PrivateLessonForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: "richardson", goal: "", sms_consent: false, email_consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const subIdRef = useRef(newSubmissionId());
  const startedRef = useRef(false);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (!startedRef.current) { startedRef.current = true; trackConversion("form_start", { form: "private_lesson" }); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leads/private-lesson`, {
        ...form,
        kind: "private-lesson",
        program: "private-lessons",
        submission_id: subIdRef.current,
        attribution: getAttribution(),
        utm_source: "guymezger.com",
        utm_campaign: "private-lessons",
      });
      trackConversion("generate_lead", { lead_type: "private_lesson", location: form.location, transaction_id: subIdRef.current });
      setDone(true);
      toast.success("Inquiry received — Hidden Gym staff will reach out shortly.");
    } catch (err) {
      trackConversion("form_error", { form: "private_lesson", code: err.response?.status || "network" });
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <div className="bg-[#0A0B0D] text-white p-8" data-testid="private-lesson-success">
        <h3 className="font-display font-black uppercase text-2xl">You're in the queue.</h3>
        <p className="mt-2 text-white/70">Hidden Gym staff will contact you within one business day to match you with the right coach and schedule your first session.</p>
      </div>
    );
  }
  const input = "w-full px-4 py-3 border border-black/20 bg-white text-[#1E2229] focus:outline-none focus:border-[#0A0B0D]";
  return (
    <form onSubmit={submit} className="grid gap-4 bg-white border border-black/10 p-6 md:p-8" data-testid="private-lesson-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="pl-name" className="block text-sm font-bold mb-1">Name</label>
          <input id="pl-name" required value={form.name} onChange={set("name")} className={input} data-testid="private-lesson-name-input" />
        </div>
        <div>
          <label htmlFor="pl-phone" className="block text-sm font-bold mb-1">Phone</label>
          <input id="pl-phone" type="tel" required value={form.phone} onChange={set("phone")} className={input} data-testid="private-lesson-phone-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="pl-email" className="block text-sm font-bold mb-1">Email</label>
          <input id="pl-email" type="email" required value={form.email} onChange={set("email")} className={input} data-testid="private-lesson-email-input" />
        </div>
        <div>
          <label htmlFor="pl-loc" className="block text-sm font-bold mb-1">Preferred location</label>
          <select id="pl-loc" value={form.location} onChange={set("location")} className={input} data-testid="private-lesson-location-select">
            {LOCATIONS.map((l) => <option key={l.slug} value={l.slug}>{l.short}</option>)}
            <option value="either">Either works</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="pl-goal" className="block text-sm font-bold mb-1">What's your goal?</label>
        <textarea id="pl-goal" required rows={3} value={form.goal} onChange={set("goal")} placeholder="e.g. Build BJJ fundamentals before joining group classes; competition prep; self-defense for my teen…" className={input} data-testid="private-lesson-goal-input" />
      </div>
      <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="private-lesson-honeypot" />
      <div className="space-y-2 text-sm text-[#374151]">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={form.sms_consent} onChange={set("sms_consent")} className="mt-1" data-testid="private-lesson-sms-consent" />
          <span>OK to text me about scheduling (SMS)</span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={form.email_consent} onChange={set("email_consent")} className="mt-1" data-testid="private-lesson-email-consent" />
          <span>OK to email me updates and offers</span>
        </label>
      </div>
      <button type="submit" disabled={busy} data-testid="private-lesson-form-submit-btn"
        className="bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide py-4 transition-colors disabled:opacity-60">
        {busy ? "Sending…" : "Request Private Lesson Info"}
      </button>
      <p className="text-xs text-[#5A6270]">Your inquiry goes directly to Hidden Gym staff. No spam, no sales pressure.</p>
    </form>
  );
};

/* ---------------- Location Cards ---------------- */
export const LocationCards = ({ withMaps = false }) => (
  <div className="grid md:grid-cols-2 gap-8">
    {LOCATIONS.map((l, idx) => (
      <Reveal key={l.slug} delay={idx * 0.1}>
        <div className="bg-white border border-black/10 flex flex-col h-full" data-testid={`location-card-${l.slug}`}>
          {withMaps && (
            <iframe title={`Map of ${l.name}`} src={l.mapEmbed} className="w-full h-64 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          )}
          <div className="p-6 md:p-8 flex flex-col flex-1">
            <h3 className="font-display font-black uppercase text-2xl leading-tight">{l.name}</h3>
            <div className="mt-3 space-y-1.5 text-[#374151] text-sm flex-1">
              <p className={l.address.startsWith("[") ? "bg-amber-100 text-amber-900 inline-block px-2 py-0.5 font-mono text-xs" : ""}>{l.address}</p>
              <p><a href={l.phoneHref} onClick={() => trackPhoneClick(l.slug, l.phoneHref)} className="hover:text-neutral-500 font-medium" data-testid={`loccard-phone-${l.slug}`}>{l.phone}</a></p>
              <p className={l.hours.startsWith("[") ? "bg-amber-100 text-amber-900 inline-block px-2 py-0.5 font-mono text-xs" : ""}>{l.hours}</p>
              <p className="text-[#5A6270]">{l.parking}</p>
              <p className="pt-2 text-xs font-mono uppercase tracking-wider text-[#5A6270]">
                Programs: {l.programs.map((p) => programBySlug(p)?.name.split(" (")[0]).join(" · ")}
              </p>
              <p className="text-sm">★ {l.rating.value.toFixed(1)} from {l.rating.count} Google reviews</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={`/locations/${l.slug}`} className="border border-[#0A0B0D] font-display font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#0A0B0D] hover:text-white transition-colors" data-testid={`location-card-${l.slug}-details-link`}>Location Details</Link>
              <a href={l.mapLink} target="_blank" rel="noopener noreferrer" className="border border-black/20 text-[#5A6270] font-display font-bold uppercase tracking-wide px-5 py-3 hover:border-black transition-colors" data-testid={`location-card-${l.slug}-directions-link`}>Get Directions</a>
            </div>
          </div>
        </div>
      </Reveal>
    ))}
  </div>
);
